/**
 * SEI 打点取证阶段的 payload 描述器。
 *
 * 后端 SEI 的真实字段目前未知，所以这里不做 JSON 解析、不假设字段名：
 * 只把「收到了多少字节、长什么样、是不是同一份」变成可上报的字段。
 * 拿到线上真实报文后再在这一层之上加语义解析，事件绑定和快照都不用动。
 */

/** hex 只留前 64 字节，足够看清报文头部结构，又不至于把埋点撑爆。 */
export const SEI_HEX_MAX_BYTES = 64;

/**
 * 原文上限。logger 的 flatten 会把超长 string 静默截到 200 字符（debug-log.ts 的
 * STRING_FIELD_MAX_LEN），静默截断看不出被截过；这里主动截到更短并带省略号，
 * 让「截断了」本身也是可观测的。
 */
export const SEI_TEXT_MAX_LEN = 128;

export interface SeiPayloadDescriptor {
  /** 原始字节数，不受 hex / 原文截断影响。 */
  byte_length: number;
  /** 前 SEI_HEX_MAX_BYTES 字节的小写 hex。 */
  hex: string;
  /** 短指纹，用于同一份 payload 去重。 */
  fingerprint: string;
  /** 全可打印时给原文，否则为 null；二进制报文不伪造文本。 */
  text: string | null;
}

const HEX_CHARS = '0123456789abcdef';

const toHex = (bytes: Uint8Array, maxBytes: number): string => {
  let out = '';
  const limit = Math.min(bytes.length, maxBytes);
  for (let index = 0; index < limit; index += 1) {
    const byte = bytes[index] ?? 0;
    out += HEX_CHARS[(byte >> 4) & 0x0f];
    out += HEX_CHARS[byte & 0x0f];
  }
  return out;
};

/**
 * djb2，只用来判「两次收到的 payload 是不是同一份」。
 * 不做任何安全用途，因此不需要加密级散列。
 */
const fingerprintOf = (bytes: Uint8Array): string => {
  let hash = 5381;
  for (let index = 0; index < bytes.length; index += 1) {
    hash = ((hash << 5) + hash + (bytes[index] ?? 0)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

/** 可打印 ASCII（含 tab / CR / LF）之外一律视为二进制。 */
const isPrintableAscii = (byte: number): boolean => {
  return (byte >= 0x20 && byte <= 0x7e) || byte === 0x09 || byte === 0x0a || byte === 0x0d;
};

const truncateText = (text: string): string => {
  return text.length > SEI_TEXT_MAX_LEN ? `${text.slice(0, SEI_TEXT_MAX_LEN - 1)}…` : text;
};

export const describeSeiPayload = (sei?: Uint8Array | null): SeiPayloadDescriptor => {
  if (!sei || sei.length === 0) {
    return { byte_length: 0, fingerprint: fingerprintOf(new Uint8Array(0)), hex: '', text: '' };
  }

  const bytes = sei instanceof Uint8Array ? sei : new Uint8Array(sei);
  let printable = true;
  for (let index = 0; index < bytes.length; index += 1) {
    if (!isPrintableAscii(bytes[index] ?? 0)) {
      printable = false;
      break;
    }
  }

  let text: string | null = null;
  if (printable) {
    let raw = '';
    for (let index = 0; index < bytes.length; index += 1) {
      raw += String.fromCharCode(bytes[index] ?? 0);
    }
    text = truncateText(raw);
  }

  return {
    byte_length: bytes.length,
    fingerprint: fingerprintOf(bytes),
    hex: toHex(bytes, SEI_HEX_MAX_BYTES),
    text,
  };
};
